import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Wrench, 
  Calendar, 
  FileText, 
  Receipt, 
  Package, 
  Archive, 
  ChevronRight,
  Shield,
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  CheckCircle,
  Building2,
  Phone,
  MapPin,
  ClipboardList,
  Calculator,
  Boxes,
  Recycle,
  PlayCircle,
  ShieldCheck
} from "lucide-react";
import { toast } from "sonner";
import italianProvinces from "../data/provinces";
import { authFetch, setAccessToken } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState("login"); // login, register, forgot, reset
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [registerStep, setRegisterStep] = useState(1); // 1: account, 2: company
  
  const [loginData, setLoginData] = useState({
    username: "",
    password: ""
  });
  
  const [registerData, setRegisterData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    user_type: "OFFICINA", // OFFICINA or RICAMBISTA
    company_name: "",
    vat_number: "",
    fiscal_code: "",
    address: "",
    zip_code: "",
    city: "",
    province: "",
    phone: "",
    pec_email: "",
    sdi_code: "",
    // Dati Ricambista (opzionali per officine)
    ricambista_company_name: "",
    ricambista_vat_number: "",
    ricambista_address: "",
    ricambista_phone: "",
    ricambista_email: "",
    privacy_accepted: false
  });

  const [forgotData, setForgotData] = useState({
    email: ""
  });

  const [resetData, setResetData] = useState({
    newPassword: "",
    confirmPassword: ""
  });

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      try {
        const response = await authFetch(`${API}/auth/me`, {
        });
        if (response.ok) {
          navigate("/dashboard");
        }
      } catch (error) {
        // Not authenticated, stay on landing
      } finally {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, [navigate]);

  // Check for reset token in URL
  useEffect(() => {
    const token = searchParams.get("reset_token");
    if (token) {
      verifyResetToken(token);
    }
  }, [searchParams]);

  const verifyResetToken = async (token) => {
    try {
      const response = await fetch(`${API}/auth/verify-reset-token?token=${token}`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setResetToken(token);
        setResetEmail(data.email);
        setAuthMode("reset");
        setShowAuthModal(true);
      } else {
        toast.error("Link di reset non valido o scaduto");
      }
    } catch (error) {
      toast.error("Errore nella verifica del link");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(loginData)
      });
      
      if (response.ok) {
        const userData = await response.json();
        
        // Store access token in memory for authFetch
        if (userData.access_token) {
          setAccessToken(userData.access_token, userData.expires_in || 900);
        }
        
        toast.success("Login effettuato con successo!");
        
        // Redirect based on role
        const role = (userData.role || "").toUpperCase();
        if (role === "RICAMBISTA") {
          navigate("/ricambista");
        } else if (role === "ADMIN") {
          navigate("/admin");
        } else {
          navigate("/dashboard");
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nel login");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Step 1 validation
    if (registerStep === 1) {
      if (registerData.password !== registerData.confirmPassword) {
        toast.error("Le password non coincidono");
        return;
      }
      if (registerData.password.length < 6) {
        toast.error("La password deve essere di almeno 6 caratteri");
        return;
      }
      setRegisterStep(2);
      return;
    }
    
    // Step 2 - Submit registration
    if (!registerData.company_name || !registerData.vat_number || !registerData.address || 
        !registerData.city || !registerData.province || !registerData.phone) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          username: registerData.username,
          email: registerData.email,
          password: registerData.password,
          name: registerData.name,
          user_type: registerData.user_type,
          company_name: registerData.company_name,
          vat_number: registerData.vat_number,
          fiscal_code: registerData.fiscal_code,
          address: registerData.address,
          zip_code: registerData.zip_code,
          city: registerData.city,
          province: registerData.province,
          phone: registerData.phone,
          pec_email: registerData.pec_email,
          sdi_code: registerData.sdi_code,
          // Dati ricambista (solo per officine)
          ricambista_company_name: registerData.ricambista_company_name || null,
          ricambista_vat_number: registerData.ricambista_vat_number || null,
          ricambista_address: registerData.ricambista_address || null,
          ricambista_phone: registerData.ricambista_phone || null,
          ricambista_email: registerData.ricambista_email || null,
          privacy_accepted: registerData.privacy_accepted
        })
      });
      
      if (response.ok) {
        toast.success("Registrazione completata! Il tuo account sarà attivato entro 48 ore.", {
          duration: 8000,
        });
        setShowAuthModal(false);
        setAuthMode("login");
        // Reset form
        setRegisterData({
          username: "",
          email: "",
          password: "",
          confirmPassword: "",
          name: "",
          user_type: "OFFICINA",
          company_name: "",
          vat_number: "",
          fiscal_code: "",
          address: "",
          zip_code: "",
          city: "",
          province: "",
          phone: "",
          pec_email: "",
          sdi_code: "",
          ricambista_company_name: "",
          ricambista_vat_number: "",
          ricambista_address: "",
          ricambista_phone: "",
          ricambista_email: "",
          privacy_accepted: false
        });
        setRegisterStep(1);
      } else {
        const error = await response.json();
        // Handle validation errors
        if (error.detail && Array.isArray(error.detail)) {
          const messages = error.detail.map(e => e.msg || e.message).join(', ');
          toast.error(messages || "Errore nella registrazione");
        } else {
          toast.error(error.detail || "Errore nella registrazione");
        }
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("Errore di connessione al server");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: forgotData.email })
      });
      
      if (response.ok) {
        // Mostra messaggio di successo e chiudi il modale
        toast.success("Se l'email è registrata, riceverai un link per reimpostare la password. Controlla anche la cartella spam.", {
          duration: 8000
        });
        setShowAuthModal(false);
        setForgotData({ email: "" });
      } else {
        // Messaggio generico per sicurezza (non rivelare se email esiste)
        toast.success("Se l'email è registrata, riceverai un link per reimpostare la password.", {
          duration: 5000
        });
      }
    } catch (error) {
      toast.error("Errore di connessione al server");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (resetData.newPassword !== resetData.confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    
    if (resetData.newPassword.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          token: resetToken,
          new_password: resetData.newPassword
        })
      });
      
      if (response.ok) {
        toast.success("Password aggiornata con successo! Effettua il login.");
        setAuthMode("login");
        setResetToken("");
        setResetData({ newPassword: "", confirmPassword: "" });
        // Clear URL params
        window.history.replaceState(null, "", "/");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nel reset della password");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 bg-primary/20 rounded-lg flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  const features = [
    { icon: Calendar, title: "Appuntamenti", desc: "Calendario intelligente con promemoria WhatsApp ai clienti" },
    { icon: Wrench, title: "Accettazione Veicoli", desc: "Check-in completo con firma digitale e rilievo danni" },
    { icon: FileText, title: "Preventivi", desc: "Invio al cliente via link e PDF, con avviso di lettura" },
    { icon: ClipboardList, title: "Ordini di Lavoro", desc: "Agenda lavori con schedulazione automatica degli operatori" },
    { icon: Receipt, title: "Fatturazione", desc: "Fatture elettroniche XML FatturaPA, note di credito e DDT" },
    { icon: Calculator, title: "Modulo Fiscale", desc: "Prima nota, registri IVA, scadenzario e analisi margini" },
    { icon: Package, title: "Richiesta Ricambi", desc: "Richieste rapide al tuo ricambista di fiducia collegato" },
    { icon: Boxes, title: "Magazzino", desc: "Articoli, giacenze, movimenti e servizi con tempario" },
    { icon: Recycle, title: "RENTRI Rifiuti", desc: "FIR digitali e tracciabilità rifiuti a norma di legge" },
    { icon: PlayCircle, title: "Video Riparazioni", desc: "Tutorial video di riparazione per il veicolo in officina" },
    { icon: Archive, title: "Archivio Documenti", desc: "Ricerca avanzata e copie PDF immutabili dei documenti inviati" },
    { icon: ShieldCheck, title: "Privacy & Backup", desc: "Dati protetti a norma GDPR con backup automatici giornalieri" }
  ];

  const renderAuthContent = () => {
    switch (authMode) {
      case "forgot":
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-white uppercase font-['Barlow_Condensed']">
                Recupera Password
              </h3>
              <p className="text-zinc-500 text-sm mt-2">
                Inserisci la tua email per ricevere il link di reset
              </p>
            </div>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  data-testid="forgot-email"
                  type="email"
                  className="form-input"
                  placeholder="Inserisci la tua email"
                  value={forgotData.email}
                  onChange={(e) => setForgotData({...forgotData, email: e.target.value})}
                  required
                />
              </div>
              <Button 
                data-testid="submit-forgot"
                type="submit" 
                className="w-full btn-primary"
                disabled={loading}
              >
                {loading ? "Invio in corso..." : "Invia Link di Reset"}
              </Button>
            </form>
            <button
              className="w-full text-center text-zinc-500 hover:text-primary text-sm mt-4 flex items-center justify-center gap-2"
              onClick={() => setAuthMode("login")}
            >
              <ArrowLeft className="w-4 h-4" />
              Torna al login
            </button>
          </div>
        );

      case "reset":
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-white uppercase font-['Barlow_Condensed']">
                Nuova Password
              </h3>
              <p className="text-zinc-500 text-sm mt-2">
                Inserisci la nuova password per <span className="text-primary">{resetEmail}</span>
              </p>
            </div>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="form-group">
                <label className="form-label">Nuova Password</label>
                <div className="relative">
                  <input
                    data-testid="reset-new-password"
                    type={showPassword ? "text" : "password"}
                    className="form-input pr-11"
                    placeholder="Minimo 6 caratteri"
                    value={resetData.newPassword}
                    onChange={(e) => setResetData({...resetData, newPassword: e.target.value})}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-0 bottom-0 flex items-center text-zinc-500 hover:text-zinc-700"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Conferma Password</label>
                <input
                  data-testid="reset-confirm-password"
                  type={showPassword ? "text" : "password"}
                  className="form-input"
                  placeholder="Ripeti la password"
                  value={resetData.confirmPassword}
                  onChange={(e) => setResetData({...resetData, confirmPassword: e.target.value})}
                  required
                />
              </div>
              <Button 
                data-testid="submit-reset"
                type="submit" 
                className="w-full btn-primary"
                disabled={loading}
              >
                {loading ? "Aggiornamento..." : "Aggiorna Password"}
              </Button>
            </form>
          </div>
        );

      case "register":
        return (
          <form onSubmit={handleRegister} className="space-y-4">
            {registerStep === 1 ? (
              <>
                {/* Step 1: Account Data */}
                <div className="text-center mb-4">
                  <span className="text-xs text-zinc-500 uppercase tracking-wide">Passaggio 1 di 2</span>
                  <h3 className="text-sm text-primary font-semibold">Dati Account</h3>
                </div>
                
                {/* User Type Selection */}
                <div className="form-group">
                  <label className="form-label">Tipo Utente *</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      className={`p-3 rounded-lg border-2 transition-all ${
                        registerData.user_type === "OFFICINA"
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                      }`}
                      onClick={() => setRegisterData({...registerData, user_type: "OFFICINA"})}
                    >
                      <Wrench className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-sm font-medium">Officina</span>
                    </button>
                    <button
                      type="button"
                      className={`p-3 rounded-lg border-2 transition-all ${
                        registerData.user_type === "RICAMBISTA"
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                      }`}
                      onClick={() => setRegisterData({...registerData, user_type: "RICAMBISTA"})}
                    >
                      <Package className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-sm font-medium">Ricambista</span>
                    </button>
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Nome Completo *</label>
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                    <input
                      data-testid="register-name"
                      type="text"
                      className="form-input flex-1"
                      placeholder="Mario Rossi"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Username *</label>
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                    <input
                      data-testid="register-username"
                      type="text"
                      className="form-input flex-1"
                      placeholder="mariorossi"
                      value={registerData.username}
                      onChange={(e) => setRegisterData({...registerData, username: e.target.value.toLowerCase().replace(/\s/g, '')})}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                    <input
                      data-testid="register-email"
                      type="email"
                      className="form-input flex-1"
                      placeholder="mario@officina.it"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Password *</label>
                  <div className="flex items-center gap-2">
                    <Lock className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                    <div className="flex-1 relative">
                      <input
                        data-testid="register-password"
                        type={showPassword ? "text" : "password"}
                        className="form-input w-full pr-10"
                        placeholder="Minimo 6 caratteri"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Conferma Password *</label>
                  <div className="flex items-center gap-2">
                    <Lock className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                    <input
                      data-testid="register-confirm-password"
                      type={showPassword ? "text" : "password"}
                      className="form-input flex-1"
                      placeholder="Ripeti la password"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                >
                  Continua <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </>
            ) : (
              <>
                {/* Step 2: Company Data */}
                <div className="text-center mb-4">
                  <span className="text-xs text-zinc-500 uppercase tracking-wide">Passaggio 2 di 2</span>
                  <h3 className="text-sm text-primary font-semibold">Dati Aziendali</h3>
                </div>
                <div className="form-group">
                  <label className="form-label">Ragione Sociale *</label>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                    <input
                      type="text"
                      className="form-input flex-1"
                      placeholder={registerData.user_type === "RICAMBISTA" ? "Ricambista Rossi Srl" : "Officina Rossi Srl"}
                      value={registerData.company_name}
                      onChange={(e) => setRegisterData({...registerData, company_name: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="form-group">
                    <label className="form-label">Partita IVA *</label>
                    <input
                      type="text"
                      className="form-input uppercase"
                      placeholder="IT12345678901"
                      value={registerData.vat_number}
                      onChange={(e) => setRegisterData({...registerData, vat_number: e.target.value.toUpperCase()})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Codice Fiscale</label>
                    <input
                      type="text"
                      className="form-input uppercase"
                      placeholder="Se diverso da P.IVA"
                      value={registerData.fiscal_code}
                      onChange={(e) => setRegisterData({...registerData, fiscal_code: e.target.value.toUpperCase()})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Indirizzo *</label>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                    <input
                      type="text"
                      className="form-input flex-1"
                      placeholder="Via Roma 1"
                      value={registerData.address}
                      onChange={(e) => setRegisterData({...registerData, address: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="form-group">
                    <label className="form-label">CAP *</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="00100"
                      maxLength={5}
                      value={registerData.zip_code}
                      onChange={(e) => setRegisterData({...registerData, zip_code: e.target.value.replace(/\D/g, '')})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Città *</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Roma"
                      value={registerData.city}
                      onChange={(e) => setRegisterData({...registerData, city: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Provincia *</label>
                    <select
                      className="form-select"
                      value={registerData.province}
                      onChange={(e) => setRegisterData({...registerData, province: e.target.value})}
                      required
                    >
                      <option value="">Seleziona</option>
                      {italianProvinces.map(p => (
                        <option key={p.code} value={p.code}>{p.code} - {p.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Telefono *</label>
                  <div className="flex items-center gap-2">
                    <Phone className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                    <input
                      type="tel"
                      className="form-input flex-1"
                      placeholder="06 1234567"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="form-group">
                    <label className="form-label">PEC</label>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="pec@officina.it"
                      value={registerData.pec_email}
                      onChange={(e) => setRegisterData({...registerData, pec_email: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Codice SDI</label>
                    <input
                      type="text"
                      className="form-input uppercase"
                      placeholder="XXXXXXX"
                      maxLength={7}
                      value={registerData.sdi_code}
                      onChange={(e) => setRegisterData({...registerData, sdi_code: e.target.value.toUpperCase()})}
                    />
                  </div>
                </div>
                
                {/* Sezione Ricambista - Solo per Officine */}
                {registerData.user_type === "OFFICINA" && (
                  <div className="mt-6 pt-6 border-t border-zinc-700">
                    <div className="flex items-start gap-3 mb-4">
                      <Package className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-semibold text-white">Dati Ricambista (Facoltativo)</h4>
                        <p className="text-xs text-zinc-400 mt-1">
                          Se indichi il tuo ricambista di fiducia, potrai essere collegato direttamente per la trasmissione automatica delle richieste di preventivo ricambi.
                        </p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 pl-8">
                      <div className="form-group">
                        <label className="form-label text-zinc-400">Ragione Sociale Ricambista</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Es. Ricambi Auto SRL"
                          value={registerData.ricambista_company_name}
                          onChange={(e) => setRegisterData({...registerData, ricambista_company_name: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label text-zinc-400">Partita IVA Ricambista</label>
                        <input
                          type="text"
                          className="form-input uppercase"
                          placeholder="IT12345678901"
                          value={registerData.ricambista_vat_number}
                          onChange={(e) => setRegisterData({...registerData, ricambista_vat_number: e.target.value.toUpperCase()})}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label text-zinc-400">Indirizzo Ricambista</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Via, Città, CAP"
                          value={registerData.ricambista_address}
                          onChange={(e) => setRegisterData({...registerData, ricambista_address: e.target.value})}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="form-group">
                          <label className="form-label text-zinc-400">Telefono</label>
                          <input
                            type="tel"
                            className="form-input"
                            placeholder="06 1234567"
                            value={registerData.ricambista_phone}
                            onChange={(e) => setRegisterData({...registerData, ricambista_phone: e.target.value})}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label text-zinc-400">Email</label>
                          <input
                            type="email"
                            className="form-input"
                            placeholder="info@ricambista.it"
                            value={registerData.ricambista_email}
                            onChange={(e) => setRegisterData({...registerData, ricambista_email: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start gap-3 mt-6 p-3 bg-zinc-900/60 border border-zinc-700 rounded-lg">
                  <input
                    data-testid="privacy-checkbox"
                    id="privacy-accept"
                    type="checkbox"
                    className="mt-0.5 w-4 h-4 accent-orange-500 cursor-pointer"
                    checked={registerData.privacy_accepted}
                    onChange={(e) => setRegisterData({...registerData, privacy_accepted: e.target.checked})}
                    required
                  />
                  <label htmlFor="privacy-accept" className="text-xs text-zinc-400 cursor-pointer">
                    Dichiaro di aver letto e accettato l'
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Informativa sulla Privacy
                    </a>{" "}
                    (Regolamento UE 2016/679) *
                  </label>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button 
                    type="button"
                    variant="outline"
                    className="flex-1 border-zinc-700"
                    onClick={() => setRegisterStep(1)}
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
                  </Button>
                  <Button 
                    data-testid="submit-register"
                    type="submit" 
                    className="flex-1 btn-primary"
                    disabled={loading}
                  >
                    {loading ? "Registrazione..." : "Completa"}
                  </Button>
                </div>
              </>
            )}
          </form>
        );

      default: // login
        return (
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="form-group">
              <label className="form-label">Username</label>
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                <input
                  data-testid="login-username"
                  type="text"
                  className="form-input flex-1"
                  placeholder="Il tuo username"
                  value={loginData.username}
                  onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                <div className="flex-1 relative">
                  <input
                    data-testid="login-password"
                    type={showPassword ? "text" : "password"}
                    className="form-input w-full pr-10"
                    placeholder="La tua password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>
            <Button 
              data-testid="submit-login"
              type="submit" 
              className="w-full btn-primary"
              disabled={loading}
            >
              {loading ? "Accesso in corso..." : "Accedi"}
            </Button>
            <button
              type="button"
              data-testid="forgot-password-link"
              className="w-full text-center text-zinc-500 hover:text-primary text-sm"
              onClick={() => setAuthMode("forgot")}
            >
              Password dimenticata?
            </button>
          </form>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background noise">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1608410865787-cae55383860e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxtZWNoYW5pYyUyMHdvcmtpbmclMjBvbiUyMGNhciUyMGluJTIwbW9kZXJuJTIwd29ya3Nob3AlMjBkYXJrJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzcwMzI3NzAzfDA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
        
        {/* Navigation */}
        <nav className="relative z-10 container mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/logo-icon.svg" 
              alt="PServicePro" 
              className="w-16 h-16"
            />
            <span className="text-3xl md:text-4xl font-bold text-white tracking-tight font-['Barlow_Condensed']">
              PServicePro
            </span>
          </div>
          <Button 
            data-testid="login-btn-nav"
            onClick={() => { setShowAuthModal(true); setAuthMode("login"); }}
            className="bg-primary text-black font-bold uppercase tracking-wide hover:bg-primary/90"
          >
            Accedi
          </Button>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-6 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl">
              <h1 className="text-5xl lg:text-7xl font-extrabold text-white uppercase leading-tight font-['Barlow_Condensed']">
                Gestione Officina
                <span className="text-primary block">Professionale</span>
              </h1>
              <p className="text-lg text-zinc-400 mt-6 max-w-xl font-['Manrope']">
                La soluzione completa per l'officina moderna: appuntamenti, accettazione, preventivi, 
                fatturazione elettronica, ricambi, magazzino e tracciabilità rifiuti RENTRI. Tutto in un'unica piattaforma.
              </p>
              <div className="flex gap-4 mt-10">
                <Button 
                  data-testid="get-started-btn"
                  onClick={() => { setShowAuthModal(true); setAuthMode("register"); }}
                  className="bg-primary text-black font-bold uppercase tracking-wide px-8 py-6 text-lg hover:bg-primary/90 glow-primary"
                >
                  Inizia Ora
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="hidden lg:block">
              <img 
                src="/hero-office.png" 
                alt="Gestione Officina" 
                className="w-full max-w-lg mx-auto rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <section className="container mx-auto px-6 py-24">
        <h2 className="text-3xl lg:text-4xl font-bold text-white uppercase text-center mb-16 font-['Barlow_Condensed']">
          Tutto ciò che ti serve
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 card-hover fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-bold text-white uppercase font-['Barlow_Condensed']">
                {feature.title}
              </h3>
              <p className="text-zinc-500 text-sm mt-2 font-['Manrope']">
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Engine Image Section */}
      <section className="container mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl lg:text-4xl font-bold text-white uppercase font-['Barlow_Condensed']">
              Precisione e<br />
              <span className="text-primary">Affidabilità</span>
            </h2>
            <p className="text-zinc-400 mt-6 font-['Manrope']">
              Progettato per professionisti che richiedono strumenti affidabili come i loro attrezzi. 
              Interfaccia intuitiva, flussi di lavoro ottimizzati, zero compromessi sulla qualità.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                "Fatturazione elettronica XML FatturaPA",
                "Trasmissione digitale FIR al sistema RENTRI",
                "Backup automatici e conformità GDPR"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-zinc-300">
                  <Shield className="w-5 h-5 text-primary" />
                  <span className="font-['Manrope']">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-primary/20 blur-3xl opacity-30" />
            <img 
              src="https://images.unsplash.com/photo-1712818323420-14eb767f24aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjYXIlMjBlbmdpbmUlMjBjbG9zZSUyMHVwJTIwZGV0YWlsfGVufDB8fHx8MTc3MDMyNzcwOXww&ixlib=rb-4.1.0&q=85"
              alt="Engine Detail"
              className="relative rounded-sm border border-zinc-800"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-6 py-24">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-12 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white uppercase font-['Barlow_Condensed']">
            Pronto per iniziare?
          </h2>
          <p className="text-zinc-400 mt-4 max-w-lg mx-auto font-['Manrope']">
            Crea il tuo account e inizia subito a gestire la tua officina in modo professionale.
          </p>
          <Button 
            data-testid="cta-register-btn"
            onClick={() => { setShowAuthModal(true); setAuthMode("register"); }}
            className="bg-primary text-black font-bold uppercase tracking-wide px-10 py-6 text-lg mt-8 hover:bg-primary/90 glow-primary"
          >
            Registrati Adesso
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8">
        <div className="container mx-auto px-6 text-center text-zinc-600 text-sm font-['Manrope']">
          © 2026 PServicePro - Gestione Officina. Tutti i diritti riservati.
          {" · "}
          <a href="/privacy" className="text-zinc-500 hover:text-primary underline">Informativa Privacy</a>
        </div>
      </footer>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => {
              if (authMode !== "reset") {
                setShowAuthModal(false);
              }
            }}
          />
          
          {/* Modal */}
          <div className="relative bg-zinc-900 border border-zinc-800 rounded-sm w-full max-w-md mx-4 p-8 max-h-[90vh] overflow-y-auto">
            {/* Logo */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <img 
                src="/logo-icon.svg" 
                alt="PServicePro" 
                className="w-12 h-12"
              />
              <span className="text-2xl font-bold text-white tracking-tight font-['Barlow_Condensed']">
                PServicePro
              </span>
            </div>

            {/* Toggle (only for login/register modes) */}
            {(authMode === "login" || authMode === "register") && (
              <div className="flex bg-zinc-950 rounded-sm p-1 mb-6">
                <button
                  data-testid="toggle-login"
                  className={`flex-1 py-2 text-sm font-bold uppercase tracking-wide rounded-sm transition-colors ${
                    authMode === "login" ? "bg-primary text-black" : "text-zinc-400 hover:text-white"
                  }`}
                  onClick={() => setAuthMode("login")}
                >
                  Accedi
                </button>
                <button
                  data-testid="toggle-register"
                  className={`flex-1 py-2 text-sm font-bold uppercase tracking-wide rounded-sm transition-colors ${
                    authMode === "register" ? "bg-primary text-black" : "text-zinc-400 hover:text-white"
                  }`}
                  onClick={() => setAuthMode("register")}
                >
                  Registrati
                </button>
              </div>
            )}

            {/* Auth Content */}
            {renderAuthContent()}

            {/* Close button (not for reset mode) */}
            {authMode !== "reset" && (
              <button
                className="absolute top-4 right-4 text-zinc-500 hover:text-white"
                onClick={() => setShowAuthModal(false)}
              >
                ✕
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
